/**
 * URL schemas for values a USER types and someone ELSE opens.
 *
 * ## Why `z.string().url()` is not enough
 *
 * Zod's `.url()` only asks whether `new URL(value)` parses. `javascript:alert(1)`,
 * `data:text/html,…` and `mailto:` all parse. So a field validated that way and
 * then rendered as an `href` hands another human a link the browser will happily
 * act on.
 *
 * That is not hypothetical here. The launch checklist's `link` shipped with a
 * bare `.url()` on a field whose entire purpose is that the founder's DOER reads
 * it (#853, fixed in `86d3ukp27`); this module generalises that fix, because a
 * sweep found the same shape in `SocialLinksSchema` — Q1.9, where the website
 * had no scheme guard and the five social fields had no validation at all.
 *
 * Checked on the SERVER rather than trusted to the client: the dashboard
 * normalises what it sends and guards what it renders, but the endpoint is
 * reachable without the dashboard.
 */
import { z } from 'zod';
/** Schemes a browser may follow from an `href` without handing off elsewhere. */
const SAFE_PROTOCOLS = new Set(['http:', 'https:']);
function hasSafeProtocol(value) {
    try {
        return SAFE_PROTOCOLS.has(new URL(value).protocol);
    }
    catch {
        return false;
    }
}
/**
 * A value that MUST be a web URL — required fields where nothing else is valid.
 */
export const webUrlSchema = z
    .string()
    .url()
    .refine(hasSafeProtocol, { message: 'Link must be an http(s) URL' });
/**
 * Schemes a browser will ACT on in a way we cannot allow from typed input.
 *
 * A denylist, not an allowlist, and that is the point for the field below.
 * `new URL()` treats anything before a colon as a scheme — `TikTok: @doneverse`
 * parses with protocol `tiktok:` — so "must be http(s) if it has a scheme"
 * rejects ordinary prose. The risk being managed is a browser following an
 * `href`, and that risk is confined to these.
 */
const DANGEROUS_PROTOCOLS = new Set([
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'blob:',
    'about:',
]);
/**
 * A social field: a handle, a URL, or prose — but never a dangerous scheme.
 *
 * The intake spec asks only for "Website (required, URL validated)" and leaves
 * Instagram/Facebook/LinkedIn/YouTube/Other loose, and the dashboard leans into
 * that: the "Anywhere else" field's own placeholder is `TikTok, podcast, …`.
 *
 * An earlier version of this required http(s) of anything containing a colon,
 * which rejected `TikTok: @doneverse` — the exact shape that placeholder asks
 * for — along with `IG: doneverse` and `Podcast: The Doing Show`. That was a
 * regression on a live question, caught by checking the schema against what the
 * form actually sends rather than against what I imagined it sent.
 *
 * So this refuses only what a browser would dangerously follow. Everything else
 * — handles, bare domains, prose, odd-but-harmless schemes — passes, because
 * for a free-text field an allowlist is the wrong tool.
 */
export const handleOrWebUrlSchema = z.string().refine((value) => {
    const trimmed = value.trim();
    if (!trimmed)
        return true;
    let protocol;
    try {
        protocol = new URL(trimmed).protocol.toLowerCase();
    }
    catch {
        // No parseable scheme at all — a handle or plain text. Nothing to follow.
        return true;
    }
    return !DANGEROUS_PROTOCOLS.has(protocol);
}, { message: 'That link uses a scheme we cannot accept' });
//# sourceMappingURL=web-urls.js.map